import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import "bootstrap/dist/css/bootstrap.min.css";
import "aos/dist/aos.css";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import AOS from "aos";

import Header from "./components/Header/Header";
import Hero from "./sections/Hero/Hero";
import NationalSection from "./sections/NationalSection/NationalSection";
import AboutGoalsSection from "./sections/AboutGoalsSection/AboutGoalsSection";
import VisionSection from "./sections/VisionSection/VisionSection";
import Services from "./sections/Services/Services";
import ProjectsSection from "./sections/ProjectsSection/ProjectsSection";
import PartnersSection from './sections/PartnersSection/PartnersSection';
import SponsorsSection from './sections/SponsorsSection/SponsorsSection';
import IsoCertificatesSection from './sections/IsoCertificatesSection/IsoCertificatesSection';
import ContactSection from './sections/ContactSection/ContactSection';
import Footer from './sections/Footer/Footer';

import AdminDashboard from './sections/Admin/AdminDashboard';
import AdminLogin from './sections/Admin/AdminLogin';

import NotFound from "./sections/NotFound/NotFound";

import { FaChevronRight, FaChevronLeft, FaTimes } from 'react-icons/fa';

// مكون لحماية المسارات
const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    const auth = localStorage.getItem('adminAuthenticated');
    setIsAuthenticated(auth === 'true');
    
    if (!auth || auth !== 'true') {
      navigate('/admin/login-m3lomgy215-frontend-backend-taamernajd');
    }
  }, [navigate]);

  if (isAuthenticated === null) {
    return <div className="d-flex justify-content-center align-items-center min-vh-100">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">جاري التحقق...</span>
      </div>
      <span className="ms-2">جاري التحقق...</span>
    </div>;
  }

  return isAuthenticated ? children : null;
};

// مكون الصفحة الرئيسية
function MainContent({ setProjectLightbox, setIsoLightbox, i18n }) {
  return (
    <>
      <Header />
      <div data-aos="fade-up"><Hero /></div>
      <div data-aos="fade-up"><NationalSection /></div>
      <div data-aos="fade-up"><AboutGoalsSection /></div>
      <div data-aos="fade-up"><VisionSection /></div>
      <div data-aos="fade-up"><Services /></div>
      <div data-aos="fade-up">
        <ProjectsSection setLightbox={setProjectLightbox} />
      </div>
      <div data-aos="fade-up"><PartnersSection /></div>
      <div data-aos="fade-up"><SponsorsSection /></div>
      <div data-aos="fade-up">
        <IsoCertificatesSection setLightbox={setIsoLightbox} />
      </div>
      <div data-aos="fade-up"><ContactSection /></div>
      <div data-aos="fade-up"><Footer /></div>
    </>
  );
}

function App() {
  const { i18n } = useTranslation();
  
  // 🔹 Popup خاص بالمشاريع (التصميم الجديد)
  const [projectLightbox, setProjectLightbox] = useState({
    isOpen: false,
    images: [],
    currentIndex: 0,
    title: ""
    // تم إزالة الحقول: location و year
  });

  // 🔹 Popup خاص بالشهادات (التصميم القديم)
  const [isoLightbox, setIsoLightbox] = useState({
    isOpen: false,
    images: [],
    currentIndex: 0
  });

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    
    // تعيين اتجاه الصفحة بناءً على اللغة الحالية
    document.body.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // 🔹 التحكم في الـ popup الخاص بالمشاريع (keyboard + scroll)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!projectLightbox.isOpen) return;
      if (e.key === 'Escape') closeProjectLightbox();
      else if (e.key === 'ArrowRight') goToNextProject();
      else if (e.key === 'ArrowLeft') goToPrevProject();
    };

    if (projectLightbox.isOpen) {
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.style.overflow = "auto";
      document.removeEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.body.style.overflow = "auto";
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [projectLightbox.isOpen]);

  // 🧠 Handlers for project popup
  const closeProjectLightbox = () => {
    setProjectLightbox(prev => ({ ...prev, isOpen: false }));
  };

  const goToPrevProject = () => {
    setProjectLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length
    }));
  };

  const goToNextProject = () => {
    setProjectLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.images.length
    }));
  };

  const goToSlideProject = (index) => {
    setProjectLightbox(prev => ({ ...prev, currentIndex: index }));
  };

  // ✅ Render project popup (new design)
  const renderProjectLightbox = () => {
    if (!projectLightbox.isOpen) return null;

    const { images, currentIndex, title } = projectLightbox;

    return (
      <div className="project-popup-overlay active" onClick={closeProjectLightbox}>
        <div className="project-popup-container" onClick={(e) => e.stopPropagation()}>
          <div className="project-popup">
            <button className="popup-close-btn" onClick={closeProjectLightbox}>
              <FaTimes />
            </button>

            {title && (
              <div className="popup-header">
                <h2 className="popup-title">{title}</h2>
                {/* تم إزالة قسم popup-meta الذي كان يحتوي على location و year */}
              </div>
            )}

            <div className="popup-content">
              <div className="popup-image-container">
                <img
                  src={images[currentIndex]}
                  alt={`${title || 'Project'} - ${currentIndex + 1}`}
                  className="popup-image"
                  loading="lazy"
                />

                {images.length > 1 && (
                  <>
                    <button 
                      className={`popup-nav-btn ${i18n.language === 'ar' ? 'next-btn' : 'prev-btn'}`} 
                      onClick={(e) => { e.stopPropagation(); goToPrevProject(); }}
                    >
                      {i18n.language === 'ar' ? <FaChevronLeft /> : <FaChevronRight />}
                    </button>
                    <button 
                      className={`popup-nav-btn ${i18n.language === 'ar' ? 'prev-btn' : 'next-btn'}`} 
                      onClick={(e) => { e.stopPropagation(); goToNextProject(); }}
                    >
                      {i18n.language === 'ar' ? <FaChevronRight /> : <FaChevronLeft />}
                    </button>

                    <div className="popup-indicators">
                      {images.map((_, index) => (
                        <div
                          key={index}
                          className={`indicator ${index === currentIndex ? 'active' : ''}`}
                          onClick={(e) => { e.stopPropagation(); goToSlideProject(index); }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ✅ Render ISO popup (old simple version)
  const renderIsoLightbox = () => {
    if (!isoLightbox.isOpen) return null;

    return (
      <div className="project-popup-overlay active" onClick={() => setIsoLightbox(prev => ({ ...prev, isOpen: false }))}>
        <button
          className="popup-close-btn"
          onClick={() => setIsoLightbox(prev => ({ ...prev, isOpen: false }))}
          aria-label="Close"
        >
          &times;
        </button>
        <div className="project-popup-container" onClick={e => e.stopPropagation()}>
          <img
            src={isoLightbox.images[isoLightbox.currentIndex]}
            alt="ISO Certificate"
            className="popup-image"
            loading="lazy"
          />
        </div>
      </div>
    );
  };

  return (
    <Router>
      <div className="app-container" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
        <Routes>
          {/* مسار الصفحة الرئيسية */}
          <Route path="/" element={
            <>
              <MainContent 
                setProjectLightbox={setProjectLightbox} 
                setIsoLightbox={setIsoLightbox} 
                i18n={i18n}
              />
              {renderProjectLightbox()}
              {renderIsoLightbox()}
            </>
          } />
          
          {/* مسار تسجيل الدخول */}
          <Route 
  path="/admin/login-m3lomgy215-frontend-backend-taamernajd" 
  element={<AdminLogin />} 
/>
          
          {/* مسار لوحة التحكم (محمي) */}
          <Route 
  path="/admin-m3lomgy215-frontend-backend-taamernajd" 
  element={
    <ProtectedRoute>
      <AdminDashboard />
    </ProtectedRoute>
  } 
/>
{/* ✅ أي رابط خاطئ يظهر 404 */}
          <Route path="*" element={<NotFound />} />
          
        </Routes>
        
      </div>
    </Router>
  );
}

export default App;